/**
 * Canonical timestamp helpers — WayAI's single representation of an instant.
 *
 * Standard (see AGENTS.md › Structure & Primitives › Timestamps):
 *   • Instants are STORED (DO SQLite) and TRANSMITTED (API/wire) as ISO-8601 UTC,
 *     millisecond precision, `Z` suffix:  `2026-06-02T15:54:00.123Z`.
 *   • The DO-store value and the wire value are byte-identical — one serializer,
 *     no stripping of the `Z`, so `new Date(value)` parses the same on every path.
 *   • Conversion to a human timezone happens ONLY at the display edge
 *     (frontend `formatInstant`, ClickHouse `toTimeZone(...)` bucketing).
 *
 * Never store or send a naive (timezone-less) timestamp. SQLite `datetime('now')`
 * (which yields `YYYY-MM-DD HH:MM:SS`, no `T`/`Z`) is banned for new columns —
 * a UTC instant without its marker gets re-parsed as local time. Use a
 * `strftime('%Y-%m-%dT%H:%M:%fZ','now')` default or write an explicit value.
 */

/** ISO-8601 UTC string, millisecond precision, `Z` suffix. The canonical form. */
export type IsoTimestamp = string;

/** Matches the canonical form exactly: `YYYY-MM-DDTHH:MM:SS.sssZ`. */
export const ISO_UTC_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/** Convert epoch milliseconds to the canonical ISO-8601 UTC string. */
export function toIso(epochMs: number): IsoTimestamp {
  return new Date(epochMs).toISOString();
}

/** Parse a canonical ISO timestamp back to epoch milliseconds. */
export function fromIso(iso: IsoTimestamp): number {
  return new Date(iso).getTime();
}

/** Current instant as a canonical ISO-8601 UTC string. */
export function nowIso(): IsoTimestamp {
  return new Date().toISOString();
}

/** True when `value` is exactly the canonical ISO-8601-UTC-with-`Z` form. */
export function isIsoTimestamp(value: unknown): value is IsoTimestamp {
  return typeof value === 'string' && ISO_UTC_RE.test(value);
}
